import { LitElement, html, svg, type CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { FormAssociatedMixin }      from '../utils/form-associated.mixin.js';
import { FormValidationController } from '../utils/form-validation.controller.js';
import styles from './styles.js';

export type CheckboxVariant = 'outlined' | 'filled';

/**
 * uwc-checkbox
 *
 * Production-grade checkbox component with W3C form association and
 * constraint validation. Set `required` to enforce the box must be checked.
 *
 * @attr checked       - Whether the checkbox is checked.
 * @attr indeterminate - Shows a dash (–) for partial selection.
 * @attr value         - Value submitted with form. Default: on.
 * @attr name          - Input name for form submission.
 * @attr label         - Label text rendered beside the checkbox.
 * @attr required      - The checkbox must be checked.
 * @attr disabled      - Disable the checkbox.
 * @attr invalid       - Mark as invalid (red border).
 * @attr variant       - outlined | filled. Default: outlined.
 *
 * @slot default - Custom label content (overrides label attr).
 *
 * @fires uwc-change  - Fired when checked state changes. Detail: { checked, value }.
 * @fires uwc-invalid - Fired when required but unchecked. Detail: { validity, validationMessage }.
 */
export class UwcCheckbox extends FormAssociatedMixin(LitElement) {

  static override styles: CSSResultGroup = [styles];

  private _validation = new FormValidationController(this);

  // ── Core ─────────────────────────────────────────────────────────

  @property({ type: Boolean, reflect: true }) checked       = false;
  @property({ type: Boolean, reflect: true }) indeterminate = false;
  @property()                                 value         = 'on';
  @property()                                 name?:  string;
  @property()                                 label?: string;

  // ── Modifiers ─────────────────────────────────────────────────────

  @property({ type: Boolean, reflect: true }) required = false;
  @property({ type: Boolean, reflect: true }) disabled = false;
  @property({ type: Boolean, reflect: true }) invalid  = false;
  @property({ reflect: true })                variant: CheckboxVariant = 'outlined';

  override firstUpdated() {
    const input = this.shadowRoot?.querySelector('input') ?? null;
    this._validation.setAnchor(input);
    this._validation.syncFormValue(this.checked ? this.value : null);
    this._validation.revalidate(this.checked ? 'on' : '', { required: this.required });
  }

  override updated(changed: Map<PropertyKey, unknown>) {
    super.updated(changed);
    if (changed.has('required')) {
      this._validation.revalidate(this.checked ? 'on' : '', { required: this.required });
    }
  }

  // ── Handlers ─────────────────────────────────────────────────────

  private _onChange(e: Event) {
    const input = e.target as HTMLInputElement;
    this.checked       = input.checked;
    this.indeterminate = false;
    this._validation.handleChange(this.checked ? 'on' : '', { required: this.required });
    this._validation.setTouched(true);
    this._validation.syncFormValue(this.checked ? this.value : null);
    this.dispatchEvent(new CustomEvent('uwc-change', {
      bubbles: true, composed: true,
      detail: { checked: this.checked, value: this.value, originalEvent: e },
    }));
  }

  // ── Icons ─────────────────────────────────────────────────────────

  private _checkIcon() {
    return svg`
      <svg class="uwc-cb__icon uwc-cb__icon--check" viewBox="0 0 12 11" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="1 5.5 4.5 9 11 1.5"/>
      </svg>`;
  }

  private _minusIcon() {
    return svg`
      <svg class="uwc-cb__icon uwc-cb__icon--minus" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
        <line x1="2" y1="6" x2="10" y2="6"/>
      </svg>`;
  }

  // ── Render ────────────────────────────────────────────────────────

  override render() {
    const labelContent = this.label
      ? html`<span part="label" class="uwc-cb__label">${this.label}</span>`
      : html`<slot part="label" class="uwc-cb__label"></slot>`;

    return html`
      <label style="display:inline-flex;align-items:center;gap:0.5rem;cursor:${this.disabled ? 'not-allowed' : 'pointer'}">
        <span part="box" class="uwc-cb">
          <input
            type="checkbox"
            .checked=${this.checked}
            .indeterminate=${this.indeterminate}
            value=${this.value}
            name=${ifDefined(this.name)}
            ?disabled=${this.disabled}
            ?required=${this.required}
            aria-required=${this.required ? 'true' : 'false'}
            @change=${this._onChange}
          />
          ${this._checkIcon()}
          ${this._minusIcon()}
        </span>
        ${labelContent}
      </label>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'uwc-checkbox': UwcCheckbox; }
}
